import React, { useState, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { theme } from '../../../../theme';
import DashboardLayout from '../../../../components/dashboard/DashboardLayout';
import { Card, CardBody } from '../../../../components/ui/Card';
import { Button } from '../../../../components/ui/Button';
import { Input } from '../../../../components/ui/Input';
import { Table } from '../../../../components/ui/Table';
import { Modal } from '../../../../components/ui/Modal';
import { useAuth } from '../../../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { FiEye, FiEdit, FiX, FiChevronRight, FiPlus, FiDownload, FiUpload, FiAlertCircle, FiPlusCircle, FiMenu, FiDollarSign } from 'react-icons/fi';
import { FaChevronLeft } from 'react-icons/fa';
import {
  fetchBankClients,
  fetchLocations,
  updateBankClient,
  formatDate,
  BankClientData,
  LocationData,
  fetchBanks,
  createBank,
  createBranch,
  BankData,
} from '../service/bankClientService';
import * as XLSX from 'xlsx';
import { Select } from '../../../../components/ui/Select';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing[6]};
`;

const PageTitle = styled.h1`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.light.text.primary};
  margin: 0;
`;

const SearchContainer = styled.div`
  display: flex;
  gap: ${theme.spacing[2]};
  margin-bottom: ${theme.spacing[4]};
  align-items: center;
  flex-wrap: nowrap;
`;

const SearchInputContainer = styled.div`
  flex: 0 0 85%;
  max-width: 85%;
  display: flex;
  align-items: stretch;
`;

const BankDropdownContainer = styled.div`
  flex: 0 0 15%;
  max-width: 15%;
  display: flex;
  align-items: stretch;
  
  & > div {
    width: 100%;
  }
`;


const FormGroup = styled.div`
  margin-bottom: ${theme.spacing[4]};
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing[4]};

  @media (max-width: ${theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const Badge = styled.span`
  display: inline-block;
  padding: ${theme.spacing[1]} ${theme.spacing[2]};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.medium};
  background-color: ${theme.colors.light.background.tertiary};
  color: ${theme.colors.light.text.secondary};
`;

const LoadingContainer = styled.div`
  text-align: center;
  padding: ${theme.spacing[12]} ${theme.spacing[6]};
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid ${theme.colors.light.border.light};
  border-top: 4px solid ${theme.colors.primary[500]};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto ${theme.spacing[4]};

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${theme.spacing[12]} ${theme.spacing[6]};
  color: ${theme.colors.light.text.secondary};
`;

const EmptyStateText = styled.p`
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.light.text.secondary};
  margin: 0;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: ${theme.spacing[3]} 0;
  border-bottom: 1px solid ${theme.colors.light.border.light};

  &:last-child {
    border-bottom: none;
  }
`;

const DetailLabel = styled.span`
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.light.text.secondary};
`;

const DetailValue = styled.span`
  color: ${theme.colors.light.text.primary};
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[2]};
  border: none;
  background-color: transparent;
  color: ${theme.colors.light.text.secondary};
  cursor: pointer;
  border-radius: ${theme.borderRadius.md};
  transition: all 0.2s ease;

  &:hover {
    background-color: ${theme.colors.light.background.tertiary};
    color: ${theme.colors.primary[600]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[6]};
`;

const StatCard = styled.div`
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.light.border.light};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[4]};
  text-align: center;
`;

const StatValue = styled.div<{ color?: string }>`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${({ color }) => color || theme.colors.primary[600]};
  margin-bottom: ${theme.spacing[1]};
`;

const StatLabel = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.light.text.secondary};
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${theme.spacing[4]};
  margin-top: ${theme.spacing[6]};
  flex-wrap: wrap;
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const PaginationInfo = styled.span`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.light.text.secondary};
`;


const ModalSection = styled.div`
  padding: ${theme.spacing[4]};
  border: 1px solid ${theme.colors.light.border.light};
  border-radius: ${theme.borderRadius.md};
  background-color: ${theme.colors.white};
`;

const HistoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
  max-height: 400px;
  overflow-y: auto;
  padding: ${theme.spacing[2]};
`;

const HistoryItem = styled.div<{ type: 'increase' | 'decrease' | 'set' }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${theme.spacing[3]};
  background: ${theme.colors.light.background.secondary};
  border-left: 4px solid ${({ type }) =>
    type === 'increase' ? theme.colors.accent.success :
      type === 'decrease' ? theme.colors.accent.danger :
        theme.colors.primary[500]};
  border-radius: ${theme.borderRadius.md};
`;

const HistoryInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const HistoryDate = styled.span`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.light.text.muted};
`;

const HistoryAmount = styled.span`
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.light.text.primary};
`;

const HistoryType = styled.span<{ type: 'increase' | 'decrease' | 'set' }>`
  font-size: ${theme.typography.fontSize.xs};
  text-transform: uppercase;
  color: ${({ type }) =>
    type === 'increase' ? theme.colors.accent.success :
      type === 'decrease' ? theme.colors.accent.danger :
        theme.colors.primary[500]};
`;

const BankClientPage: React.FC = () => {
  const { user, companyData, staffData } = useAuth();
  const [clients, setClients] = useState<BankClientData[]>([]);
  const [locations, setLocations] = useState<LocationData[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClient, setSelectedClient] = useState<BankClientData | null>(null);
  const [updatedClient, setUpdatedClient] = useState<BankClientData | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 15; // Fixed page size
  const [selectedBank, setSelectedBank] = useState<string>('');

  // New states for Bank and Branch creation
  const [showAddBankModal, setShowAddBankModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [banks, setBanks] = useState<BankData[]>([]);
  const [newBankName, setNewBankName] = useState('');
  const [isAddingNewBank, setIsAddingNewBank] = useState(false);
  const [selectedBankId, setSelectedBankId] = useState('');
  const [newBranchData, setNewBranchData] = useState({
    branchCode: '',
    address: '',
    mobile: '',
    locationId: '',
    requiredGuard: 3,
    psoName: '',
    psoEmail: '',
    psoNumber: '',
    bomName: '',
    bomEmail: '',
    bomNumber: '',
    salary: 0,
  });
  const [importing, setImporting] = useState(false);
  const [showSalaryHistoryModal, setShowSalaryHistoryModal] = useState(false);
  const [selectedHistoryClient, setSelectedHistoryClient] = useState<BankClientData | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Check permissions
  const userEmail = user?.email?.toLowerCase();
  const isCompanyOwner = !!companyData && (
    !staffData ||
    (userEmail && (
      companyData.email?.toLowerCase() === userEmail ||
      companyData.emails?.some((email: string) => email.toLowerCase() === userEmail)
    ))
  );

  const canViewClients = isCompanyOwner || (staffData?.access?.clients === true);
  const canEditClients = isCompanyOwner || (staffData?.access?.clients === true && (staffData?.permissions?.clients === 'edit' || staffData?.permissions?.clients === 'add' || staffData?.permissions?.clients === 'delete'));

  // Get company ID
  const companyId = companyData?.id || '';

  // Get unique banks for dropdown
  const uniqueBanks = useMemo(() => {
    const banks = new Set<string>();
    clients.forEach((c) => {
      if (c.bankName) {
        banks.add(c.bankName);
      }
    });
    return Array.from(banks).sort();
  }, [clients]);

  // Auto-filter clients based on search term and selected bank
  const filteredClients = useMemo(() => {
    let filtered = clients;

    // Filter by selected bank
    if (selectedBank) {
      filtered = filtered.filter((c) => c.bankName === selectedBank);
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((c) => {
        const name = c.bankName?.toLowerCase() || '';
        const branchCode = c.branchCode ? String(c.branchCode).toLowerCase() : '';
        const address = c.address?.toLowerCase() || '';
        return name.includes(term) || branchCode.includes(term) || address.includes(term);
      });
    }

    return filtered;
  }, [searchTerm, selectedBank, clients]);

  // Paginate filtered results
  const paginatedClients = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    return filteredClients.slice(start, end);
  }, [filteredClients, currentPage, pageSize]);

  // Calculate summary statistics
  const totalClients = clients.length;
  const uniqueBanksCount = new Set(clients.map((c) => c.bankId)).size;
  const clientsWithMobile = clients.filter((c) => c.mobile).length;
  const clientsWithAddress = clients.filter((c) => c.address).length;

  // Reset to page 1 when search term or selected bank changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedBank]);

  useEffect(() => {
    if (user && canViewClients) {
      loadData();
      loadBanks();
    }
  }, [user, companyData, canViewClients]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [clientsData, locationsData] = await Promise.all([
        fetchBankClients(companyId),
        fetchLocations(),
      ]);
      setClients(clientsData);
      setLocations(locationsData);
    } catch (error: any) {
      console.error('Error loading data:', error);
      toast.error(error.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadBanks = async () => {
    try {
      const banksData = await fetchBanks(companyId);
      setBanks(banksData);
    } catch (error) {
      console.error('Error loading banks:', error);
    }
  };

  const handleCreateOnlyBank = async () => {
    if (!newBankName.trim()) {
      toast.error('Please enter a bank name');
      return;
    }

    setLoading(true);
    try {
      await createBank(newBankName.trim(), companyId);
      toast.success('Bank created successfully');
      setNewBankName('');
      await loadBanks();
    } catch (error: any) {
      toast.error(error.message || 'Failed to create bank');
    } finally {
      setLoading(false);
    }
  };

  const handleAddBankAndBranch = async () => {
    if (!selectedBankId && !newBankName.trim()) {
      toast.error('Please select a bank or enter a new bank name');
      return;
    }

    if (!newBranchData.branchCode.trim()) {
      toast.error('Branch code is required');
      return;
    }

    setLoading(true);
    try {
      let bankId = selectedBankId;
      if (isAddingNewBank && newBankName.trim()) {
        bankId = await createBank(newBankName.trim(), companyId);
      }

      await createBranch(bankId, newBranchData, companyId);
      toast.success('Bank branch added successfully');
      setShowAddBankModal(false);
      resetAddForm();
      await loadData();
      await loadBanks();
    } catch (error: any) {
      toast.error(error.message || 'Failed to add bank/branch');
    } finally {
      setLoading(false);
    }
  };

  const resetAddForm = () => {
    setSelectedBankId('');
    setNewBankName('');
    setIsAddingNewBank(false);
    setNewBranchData({
      branchCode: '',
      address: '',
      mobile: '',
      locationId: '',
      requiredGuard: 3,
      psoName: '',
      psoEmail: '',
      psoNumber: '',
      bomName: '',
      bomEmail: '',
      bomNumber: '',
      salary: 0,
    });
  };

  const handleDownloadTemplate = () => {
    const template = [
      {
        'Bank Name': 'Example Bank',
        'Branch Code': 'BR-001',
        'Address': '123 Main St, City',
        'Mobile': '0300-1234567',
        'Location Name': 'Downtown',
        'Required Guards': 3
      }
    ];

    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Branches');
    XLSX.writeFile(wb, 'Bank_Branches_Template.xlsx');
  };

  const handleImportExcel = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);
    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const bstr = evt.target?.result;
        const wb = XLSX.read(bstr, { type: 'binary' });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const data = (XLSX.utils as any).sheet_to_json(ws) as any[];

        if (data.length === 0) {
          toast.error('Excel file is empty');
          setImporting(false);
          return;
        }

        let successCount = 0;
        let errorCount = 0;

        for (const row of data) {
          const bankName = row['Bank Name'];
          const branchCode = row['Branch Code'];
          const address = row['Address'] || '';
          const mobile = row['Mobile'] || '';
          const locationName = row['Location Name'] || '';
          const requiredGuard = row['Required Guards'] || 3;

          if (!bankName || !branchCode) {
            errorCount++;
            continue;
          }

          try {
            // Find or create bank
            let bank = banks.find(b => b.name.toLowerCase() === bankName.toLowerCase());
            let bankId = bank?.id;

            if (!bankId) {
              bankId = await createBank(bankName, companyId);
              // Update local banks list to avoid re-creating the same bank in next iteration
              const newBank = { id: bankId, name: bankName };
              setBanks(prev => [...prev, newBank]);
              banks.push(newBank);
            }

            // Find location ID by name
            const location = locations.find(l => l.name.toLowerCase() === locationName.toLowerCase());

            await createBranch(bankId, {
              branchCode,
              address,
              mobile,
              locationId: location?.id || '',
              requiredGuard: parseInt(requiredGuard.toString()) || 3,
            }, companyId);

            successCount++;
          } catch (err) {
            console.error('Error importing row:', err);
            errorCount++;
          }
        }

        toast.success(`Import complete! Success: ${successCount}, Errors: ${errorCount}`);
        setShowAddBankModal(false);
        await loadData();
      } catch (err) {
        console.error('Error reading excel:', err);
        toast.error('Failed to read Excel file');
      } finally {
        setImporting(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    };
    reader.readAsBinaryString(file);
  };

  const handleReset = () => {
    setSearchTerm('');
    setSelectedBank('');
    setCurrentPage(1);
  };

  const handleNextPage = () => {
    const totalPages = Math.ceil(filteredClients.length / pageSize);
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const handleFirstPage = () => {
    setCurrentPage(1);
  };

  const handleLastPage = () => {
    const totalPages = Math.ceil(filteredClients.length / pageSize);
    setCurrentPage(totalPages || 1);
  };

  const openViewModal = (client: BankClientData) => {
    setSelectedClient(client);
    setUpdatedClient({ ...client });
    setIsEditing(false);
    setShowModal(true);
  };

  const handleFieldChange = (field: keyof BankClientData, value: any) => {
    setUpdatedClient((prev) => (prev ? { ...prev, [field]: value } : null));
  };

  const startEditing = () => {
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setUpdatedClient(selectedClient ? { ...selectedClient } : null);
  };

  const saveUpdate = async () => {
    if (!updatedClient || !selectedClient) return;

    if (!updatedClient.branchCode?.trim()) {
      toast.error('Branch code is required');
      return;
    }

    setLoading(true);
    try {
      await updateBankClient(
        selectedClient.branchId,
        selectedClient.bankId,
        updatedClient,
        companyId
      );
      toast.success('Bank client updated successfully');
      await loadData();
      setIsEditing(false);
      setShowModal(false);
    } catch (error: any) {
      console.error('Update error:', error);
      toast.error(error.message || 'Update failed');
    } finally {
      setLoading(false);
    }
  };

  // If no view access, show permission denied message
  if (!canViewClients) {
    return (
      <DashboardLayout>
        <Card>
          <CardBody>
            <EmptyState>
              <EmptyStateText>You don't have permission to view bank clients.</EmptyStateText>
            </EmptyState>
          </CardBody>
        </Card>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <PageHeader>
        <PageTitle>Bank Clients</PageTitle>
        <div style={{ display: 'flex', gap: theme.spacing[2] }}>
          {canEditClients && (
            <>
              <Button variant="secondary" onClick={handleDownloadTemplate}>
                <FiDownload size={18} style={{ marginRight: theme.spacing[2] }} />
                Template
              </Button>
              <Button variant="secondary" onClick={() => setShowImportModal(true)}>
                <FiUpload size={18} style={{ marginRight: theme.spacing[2] }} />
                Import
              </Button>
              <Button variant="primary" onClick={() => setShowAddBankModal(true)}>
                <FiPlus size={18} style={{ marginRight: theme.spacing[2] }} />
                Add Bank/Branch
              </Button>
            </>
          )}
        </div>
      </PageHeader>

      {/* Summary Cards */}
      <StatsContainer>
        <StatCard>
          <StatValue>{totalClients}</StatValue>
          <StatLabel>Total Branches</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue color={theme.colors.primary[600]}>{uniqueBanksCount}</StatValue>
          <StatLabel>Total Banks</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue color={theme.colors.accent.success}>{clientsWithMobile}</StatValue>
          <StatLabel>With Mobile</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue color={theme.colors.primary[500]}>{clientsWithAddress}</StatValue>
          <StatLabel>With Address</StatLabel>
        </StatCard>
      </StatsContainer>

      <Card>
        <CardBody>
          <SearchContainer>
            <SearchInputContainer>
              <Input
                placeholder="Search by bank name, branch code, or address..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                dark
                fullWidth
              />
            </SearchInputContainer>
            <BankDropdownContainer>
              <Select
                value={selectedBank}
                onChange={(e) => {
                  setSelectedBank(e.target.value);
                  setCurrentPage(1);
                }}
                dark
              >
                <option value="">All Banks</option>
                {uniqueBanks.map((bank) => (
                  <option key={bank} value={bank}>
                    {bank}
                  </option>
                ))}
              </Select>
            </BankDropdownContainer>
            {(searchTerm || selectedBank) && (
              <div style={{ flexShrink: 0, marginLeft: theme.spacing[2] }}>
                <Button variant="secondary" onClick={handleReset} disabled={loading}>
                  <FiX size={18} />
                  Clear
                </Button>
              </div>
            )}
          </SearchContainer>

          {loading && filteredClients.length === 0 ? (
            <LoadingContainer>
              <Spinner />
              <p style={{ color: theme.colors.light.text.secondary }}>Loading bank clients...</p>
            </LoadingContainer>
          ) : filteredClients.length > 0 ? (
            <>
              <Table>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>Bank</Table.HeaderCell>
                    <Table.HeaderCell>Branch Code</Table.HeaderCell>
                    <Table.HeaderCell>Mobile</Table.HeaderCell>
                    <Table.HeaderCell>Address</Table.HeaderCell>
                    <Table.HeaderCell>Location</Table.HeaderCell>
                    <Table.HeaderCell>Client Code</Table.HeaderCell>
                    <Table.HeaderCell>Actions</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {paginatedClients.map((client) => (
                    <Table.Row key={client.branchId}>
                      <Table.Cell>{client.bankName || '—'}</Table.Cell>
                      <Table.Cell>{client.branchCode || '—'}</Table.Cell>
                      <Table.Cell>{client.mobile || '—'}</Table.Cell>
                      <Table.Cell>{client.address || '—'}</Table.Cell>
                      <Table.Cell>{client.locationName || 'N/A'}</Table.Cell>
                      <Table.Cell>
                        <Badge>{client.securityCode || 'N/A'}</Badge>
                      </Table.Cell>
                      <Table.Cell>
                        <ActionButton
                          onClick={() => {
                            setSelectedHistoryClient(client);
                            setShowSalaryHistoryModal(true);
                          }}
                          title="Manage Salary & History"
                        >
                          <FiDollarSign size={18} />
                        </ActionButton>
                        <ActionButton
                          onClick={() => openViewModal(client)}
                          title="View Details"
                        >
                          <FiEye size={18} />
                        </ActionButton>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>

              <PaginationContainer>
                <PaginationInfo>
                  Showing {paginatedClients.length > 0 ? (currentPage - 1) * pageSize + 1 : 0} to{' '}
                  {Math.min(currentPage * pageSize, filteredClients.length)} of {filteredClients.length} results
                </PaginationInfo>
                <PaginationControls>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleFirstPage}
                    disabled={currentPage === 1 || loading}
                    title="First Page"
                  >
                    <FaChevronLeft size={16} />
                    <FaChevronLeft size={16} style={{ marginLeft: -10 }} />
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handlePrevPage}
                    disabled={currentPage === 1 || loading}
                  >
                    <FaChevronLeft size={18} />
                    Previous
                  </Button>
                  <PaginationInfo>
                    Page {currentPage} of {Math.ceil(filteredClients.length / pageSize) || 1}
                  </PaginationInfo>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleNextPage}
                    disabled={currentPage >= Math.ceil(filteredClients.length / pageSize) || loading}
                  >
                    Next
                    <FiChevronRight size={18} />
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleLastPage}
                    disabled={currentPage >= Math.ceil(filteredClients.length / pageSize) || loading}
                    title="Last Page"
                  >
                    <FiChevronRight size={16} />
                    <FiChevronRight size={16} style={{ marginRight: -10 }} />
                  </Button>
                </PaginationControls>
              </PaginationContainer>
            </>
          ) : (
            <EmptyState>
              <EmptyStateText>No bank clients found.</EmptyStateText>
            </EmptyState>
          )}
        </CardBody>
      </Card>

      {/* View/Edit Modal */}
      {selectedClient && updatedClient && (
        <Modal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            setSelectedClient(null);
            setUpdatedClient(null);
          }}
          title={isEditing ? 'Edit Bank Branch' : updatedClient.bankName || 'Bank Client'}
          footer={
            <>
              {!isEditing ? (
                <>
                  <Button variant="secondary" onClick={() => setShowModal(false)}>
                    Close
                  </Button>
                  {canEditClients && (
                    <Button onClick={startEditing}>
                      <FiEdit size={18} style={{ marginRight: theme.spacing[2] }} />
                      Edit
                    </Button>
                  )}
                </>
              ) : (
                <>
                  <Button variant="secondary" onClick={cancelEditing} disabled={loading}>
                    Cancel
                  </Button>
                  <Button onClick={saveUpdate} disabled={loading} loading={loading}>
                    Update
                  </Button>
                </>
              )}
            </>
          }
        >
          <FormGroup>
            <label style={{
              display: 'block',
              fontSize: theme.typography.fontSize.sm,
              fontWeight: theme.typography.fontWeight.medium,
              color: theme.colors.light.text.primary,
              marginBottom: theme.spacing[1]
            }}>
              Bank Name
            </label>
            <Input
              value={updatedClient.bankName || ''}
              disabled
              dark
              fullWidth
            />
          </FormGroup>

          <FormGroup>
            <Input
              label="Branch Code"
              value={updatedClient.branchCode || ''}
              onChange={(e) => handleFieldChange('branchCode', e.target.value)}
              disabled={!isEditing}
              dark
              fullWidth
            />
          </FormGroup>

          <FormGroup>
            <Input
              label="Mobile"
              value={updatedClient.mobile || ''}
              onChange={(e) => handleFieldChange('mobile', e.target.value)}
              disabled={!isEditing}
              dark
              fullWidth
            />
          </FormGroup>

          <FormGroup>
            <Input
              label="Address"
              value={updatedClient.address || ''}
              onChange={(e) => handleFieldChange('address', e.target.value)}
              disabled={!isEditing}
              dark
              fullWidth
            />
          </FormGroup>

          <FormGroup>
            <Input
              label="Required Guards"
              type="number"
              value={String(updatedClient.requiredGuard || 3)}
              disabled={!isEditing}
              onChange={(e) => handleFieldChange('requiredGuard', parseInt(e.target.value) || 3)}
              dark
              fullWidth
            />
          </FormGroup>

          <FormRow>
            <FormGroup>
              <Input
                label="PSO Name"
                value={updatedClient.psoName || ''}
                onChange={(e) => handleFieldChange('psoName', e.target.value)}
                disabled={!isEditing}
                dark
                fullWidth
              />
            </FormGroup>
            <FormGroup>
              <Input
                label="PSO Email"
                value={updatedClient.psoEmail || ''}
                onChange={(e) => handleFieldChange('psoEmail', e.target.value)}
                disabled={!isEditing}
                dark
                fullWidth
              />
            </FormGroup>
          </FormRow>

          <FormGroup>
            <Input
              label="PSO Number"
              value={updatedClient.psoNumber || ''}
              onChange={(e) => handleFieldChange('psoNumber', e.target.value)}
              disabled={!isEditing}
              dark
              fullWidth
            />
          </FormGroup>

          <FormRow>
            <FormGroup>
              <Input
                label="BOM Name"
                value={updatedClient.bomName || ''}
                onChange={(e) => handleFieldChange('bomName', e.target.value)}
                disabled={!isEditing}
                dark
                fullWidth
              />
            </FormGroup>
            <FormGroup>
              <Input
                label="BOM Email"
                value={updatedClient.bomEmail || ''}
                onChange={(e) => handleFieldChange('bomEmail', e.target.value)}
                disabled={!isEditing}
                dark
                fullWidth
              />
            </FormGroup>
          </FormRow>

          <FormGroup>
            <Input
              label="BOM Number"
              value={updatedClient.bomNumber || ''}
              onChange={(e) => handleFieldChange('bomNumber', e.target.value)}
              disabled={!isEditing}
              dark
              fullWidth
            />
          </FormGroup>

          <FormGroup>
            <Input
              label="Salary (Rs.)"
              type="number"
              value={String(updatedClient.salary || 0)}
              disabled={!isEditing}
              onChange={(e) => handleFieldChange('salary', parseFloat(e.target.value) || 0)}
              dark
              fullWidth
            />
          </FormGroup>

          <FormGroup>
            <label style={{
              display: 'block',
              fontSize: theme.typography.fontSize.sm,
              fontWeight: theme.typography.fontWeight.medium,
              color: theme.colors.light.text.primary,
              marginBottom: theme.spacing[1]
            }}>
              Location
            </label>
            <Select
              value={updatedClient.locationId || ''}
              disabled={!isEditing}
              onChange={(e) => handleFieldChange('locationId', e.target.value)}
              dark
              fullWidth
            >
              <option value="">Select Location</option>
              {locations.map((loc) => (
                <option key={loc.id} value={loc.id}>
                  {loc.name}
                </option>
              ))}
            </Select>
          </FormGroup>

          <FormGroup>
            <label style={{
              display: 'block',
              fontSize: theme.typography.fontSize.sm,
              fontWeight: theme.typography.fontWeight.medium,
              color: theme.colors.light.text.primary,
              marginBottom: theme.spacing[1]
            }}>
              Client Code
            </label>
            <Input
              value={updatedClient.securityCode || 'N/A'}
              disabled
              dark
              fullWidth
            />
          </FormGroup>

          {updatedClient.dateOfRegistration && (
            <FormGroup>
              <label style={{
                display: 'block',
                fontSize: theme.typography.fontSize.sm,
                fontWeight: theme.typography.fontWeight.medium,
                color: theme.colors.light.text.primary,
                marginBottom: theme.spacing[1]
              }}>
                Date of Registration
              </label>
              <Input
                value={formatDate(updatedClient.dateOfRegistration)}
                disabled
                dark
                fullWidth
              />
            </FormGroup>
          )}
        </Modal>
      )}

      {/* Add Bank/Branch Modal */}
      <Modal
        isOpen={showAddBankModal}
        onClose={() => {
          setShowAddBankModal(false);
          resetAddForm();
        }}
        title="Add Bank Branch"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowAddBankModal(false)} disabled={loading}>
              Cancel
            </Button>
            <Button onClick={handleAddBankAndBranch} loading={loading} disabled={loading}>
              Add Branch
            </Button>
          </>
        }
      >
        <FormGroup>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: theme.spacing[1] }}>
            <label style={{ fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.medium, color: theme.colors.light.text.primary }}>
              Bank *
            </label>
            <Button
              variant="ghost"
              size="sm"
              dark
              onClick={() => setIsAddingNewBank(!isAddingNewBank)}
              style={{ padding: '2px 8px', minHeight: 'auto', fontSize: '12px' }}
            >
              {isAddingNewBank ? (
                <><FiMenu size={14} style={{ marginRight: '4px' }} /> Select Existing</>
              ) : (
                <><FiPlusCircle size={14} style={{ marginRight: '4px' }} /> Add New Bank</>
              )}
            </Button>
          </div>

          {isAddingNewBank ? (
            <div style={{ display: 'flex', gap: theme.spacing[2], alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}>
                <Input
                  placeholder="Enter new bank name"
                  value={newBankName}
                  onChange={(e) => setNewBankName(e.target.value)}
                  dark
                  fullWidth
                />
              </div>
              <Button
                variant="primary"
                size="sm"
                onClick={handleCreateOnlyBank}
                loading={loading}
                disabled={loading || !newBankName.trim()}
                style={{ marginBottom: '2px' }}
              >
                Save Bank
              </Button>
            </div>
          ) : (
            <Select
              value={selectedBankId}
              onChange={(e) => setSelectedBankId(e.target.value)}
              dark
              fullWidth
            >
              <option value="">Select an existing bank</option>
              {banks.map(bank => (
                <option key={bank.id} value={bank.id}>{bank.name}</option>
              ))}
            </Select>
          )}
        </FormGroup>

        <FormGroup>
          <Input
            label="Branch Code *"
            placeholder="e.g. BR-001"
            value={newBranchData.branchCode}
            onChange={(e) => setNewBranchData({ ...newBranchData, branchCode: e.target.value })}
            dark
            fullWidth
          />
        </FormGroup>

        <FormRow>
          <FormGroup>
            <Input
              label="Address"
              placeholder="Full address"
              value={newBranchData.address}
              onChange={(e) => setNewBranchData({ ...newBranchData, address: e.target.value })}
              dark
              fullWidth
            />
          </FormGroup>
          <FormGroup>
            <Input
              label="Mobile"
              placeholder="Contact number"
              value={newBranchData.mobile}
              onChange={(e) => setNewBranchData({ ...newBranchData, mobile: e.target.value })}
              dark
              fullWidth
            />
          </FormGroup>
        </FormRow>

        <FormRow>
          <FormGroup>
            <Select
              label="Location"
              value={newBranchData.locationId}
              onChange={(e) => setNewBranchData({ ...newBranchData, locationId: e.target.value })}
              dark
              fullWidth
            >
              <option value="">Select location</option>
              {locations.map(loc => (
                <option key={loc.id} value={loc.id}>{loc.name}</option>
              ))}
            </Select>
          </FormGroup>
          <FormGroup>
            <Input
              label="Required Guards"
              type="text"
              value={newBranchData.requiredGuard.toString()}
              onChange={(e) => {
                const val = parseInt(e.target.value) || 0;
                setNewBranchData({ ...newBranchData, requiredGuard: val });
              }}
              dark
              fullWidth
            />
          </FormGroup>

          <FormGroup>
            <Input
              label="Branch Salary (Rs.)"
              type="number"
              placeholder="Salary for guards"
              value={newBranchData.salary.toString()}
              onChange={(e) => {
                const val = parseFloat(e.target.value) || 0;
                setNewBranchData({ ...newBranchData, salary: val });
              }}
              dark
              fullWidth
            />
          </FormGroup>
        </FormRow>

        <FormRow>
          <FormGroup>
            <Input
              label="PSO Name"
              placeholder="PSO Name"
              value={newBranchData.psoName}
              onChange={(e) => setNewBranchData({ ...newBranchData, psoName: e.target.value })}
              dark
              fullWidth
            />
          </FormGroup>
          <FormGroup>
            <Input
              label="PSO Email"
              placeholder="PSO Email"
              value={newBranchData.psoEmail}
              onChange={(e) => setNewBranchData({ ...newBranchData, psoEmail: e.target.value })}
              dark
              fullWidth
            />
          </FormGroup>
        </FormRow>

        <FormGroup>
          <Input
            label="PSO Number"
            placeholder="PSO Number"
            value={newBranchData.psoNumber}
            onChange={(e) => setNewBranchData({ ...newBranchData, psoNumber: e.target.value })}
            dark
            fullWidth
          />
        </FormGroup>

        <FormRow>
          <FormGroup>
            <Input
              label="BOM Name"
              placeholder="BOM Name"
              value={newBranchData.bomName}
              onChange={(e) => setNewBranchData({ ...newBranchData, bomName: e.target.value })}
              dark
              fullWidth
            />
          </FormGroup>
          <FormGroup>
            <Input
              label="BOM Email"
              placeholder="BOM Email"
              value={newBranchData.bomEmail}
              onChange={(e) => setNewBranchData({ ...newBranchData, bomEmail: e.target.value })}
              dark
              fullWidth
            />
          </FormGroup>
        </FormRow>

        <FormGroup>
          <Input
            label="BOM Number"
            placeholder="BOM Number"
            value={newBranchData.bomNumber}
            onChange={(e) => setNewBranchData({ ...newBranchData, bomNumber: e.target.value })}
            dark
            fullWidth
          />
        </FormGroup>
      </Modal>

      {/* Salary History Modal */}
      <Modal
        isOpen={showSalaryHistoryModal}
        onClose={() => {
          setShowSalaryHistoryModal(false);
          setSelectedHistoryClient(null);
        }}
        title={`Salary Management - ${selectedHistoryClient?.bankName || ''} (${selectedHistoryClient?.branchCode || ''})`}
        footer={
          <Button variant="secondary" onClick={() => setShowSalaryHistoryModal(false)}>
            Close
          </Button>
        }
      >
        <div style={{ padding: theme.spacing[4] }}>
          <div style={{ marginBottom: theme.spacing[6], padding: theme.spacing[4], background: theme.colors.light.background.secondary, borderRadius: theme.borderRadius.lg }}>
            <h3 style={{ margin: `0 0 ${theme.spacing[3]} 0`, fontSize: theme.typography.fontSize.sm, color: theme.colors.light.text.secondary }}>Set New Salary</h3>
            <div style={{ display: 'flex', gap: theme.spacing[2] }}>
              <div style={{ flex: 1 }}>
                <Input
                  type="number"
                  placeholder="Enter new salary"
                  value={selectedHistoryClient?.salary?.toString() || '0'}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value) || 0;
                    setSelectedHistoryClient(prev => prev ? { ...prev, salary: val } : null);
                  }}
                  dark
                  fullWidth
                />
              </div>
              <Button
                variant="primary"
                onClick={async () => {
                  if (!selectedHistoryClient) return;
                  try {
                    setLoading(true);
                    await updateBankClient(
                      selectedHistoryClient.branchId,
                      selectedHistoryClient.bankId,
                      { salary: selectedHistoryClient.salary },
                      companyId
                    );
                    toast.success('Salary updated and history recorded');
                    await loadData();
                    // Update local state to reflect history change without re-fetching everything if possible, 
                    // but loadData is safer.
                    setShowSalaryHistoryModal(false);
                  } catch (error) {
                    toast.error('Failed to update salary');
                  } finally {
                    setLoading(false);
                  }
                }}
                loading={loading}
                disabled={loading}
              >
                Update
              </Button>
            </div>
          </div>

          <h3 style={{ margin: `0 0 ${theme.spacing[3]} 0`, fontSize: theme.typography.fontSize.sm, color: theme.colors.light.text.secondary }}>Salary History</h3>
          <HistoryList>
            {selectedHistoryClient?.salaryHistory && selectedHistoryClient.salaryHistory.length > 0 ? (
              [...selectedHistoryClient.salaryHistory].reverse().map((entry, index) => (
                <HistoryItem key={index} type={entry.type}>
                  <HistoryInfo>
                    <HistoryAmount>Rs. {entry.amount.toLocaleString()}</HistoryAmount>
                    <HistoryDate>{formatDate(entry.date)}</HistoryDate>
                  </HistoryInfo>
                  <HistoryType type={entry.type}>
                    {entry.type === 'set' ? 'Initial' :
                      entry.type === 'increase' ? `+Rs. ${(entry.amount - (entry.previousAmount || 0)).toLocaleString()}` :
                        `-Rs. ${((entry.previousAmount || 0) - entry.amount).toLocaleString()}`}
                  </HistoryType>
                </HistoryItem>
              ))
            ) : (
              <EmptyState style={{ padding: theme.spacing[4] }}>
                <EmptyStateText>No salary history available.</EmptyStateText>
              </EmptyState>
            )}
          </HistoryList>
        </div>
      </Modal>

      {/* Import Modal */}
      <Modal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        title="Import Branches from Excel"
        footer={
          <Button variant="secondary" onClick={() => setShowImportModal(false)} disabled={importing}>
            Close
          </Button>
        }
      >
        <div style={{ padding: theme.spacing[4], textAlign: 'center' }}>
          <FiAlertCircle size={48} style={{ color: theme.colors.accent.info, marginBottom: theme.spacing[4] }} />
          <p style={{ marginBottom: theme.spacing[4], color: theme.colors.light.text.primary }}>
            Upload an Excel file with the following columns:<br />
            <strong>Bank Name, Branch Code, Address, Mobile, Location Name, Required Guards</strong>
          </p>

          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImportExcel}
            accept=".xlsx, .xls"
            style={{ display: 'none' }}
          />

          <Button
            onClick={() => fileInputRef.current?.click()}
            loading={importing}
            disabled={importing}
            fullWidth
          >
            <FiUpload size={18} style={{ marginRight: theme.spacing[2] }} />
            {importing ? 'Importing...' : 'Select Excel File'}
          </Button>
        </div>
      </Modal>
    </DashboardLayout>
  );
};

export default BankClientPage;

